import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { pricesQueryParams } from "@onesolution/components";
import { authMiddleware, mondayBoards } from "@onesolution/middlewares";

import { BOARDS_KEY_MAPPER_ENUM, TASKS_COLUMNS } from "@onesolution/constants";
import { PROJECT_COLUMNS } from "@onesolution/constants/monday/projects";
import type { MONDAY_TYPES } from "@onesolution/monday";
import { mondayClient } from "@onesolution/monday/monday";
import computeMondayString from "@onesolution/utils/computeMondayString";
import {
	filterMessages,
	getStatusChoicesFromServer,
	wrapWithUseQuery,
} from "..";
import { formatGlobalStatus } from "../../utils/formatGlobalStatus";
import { getItemPageByValuesFromServer } from "../getItemPageByValues";

const OperationQueryParams = z.object({
	id: z.string(),
});

const handler = createServerFn()
	.middleware([authMiddleware, mondayBoards])
	.validator(OperationQueryParams)
	.handler(async ({ data, context }) => {
		const operation = await mondayClient.query.operationDetails({
			id: data.id,
		});

		const originalSubItems = await getItemPageByValuesFromServer({
			data: pricesQueryParams,
		});

		const projectIds = operation.products?.linked_item_ids || [];
		const baseProjects = await mondayClient.query.items<{
			status: MONDAY_TYPES["text"];
			statusGlobal: MONDAY_TYPES["text"];
			deadline: MONDAY_TYPES["date"];
			ziflow: MONDAY_TYPES["text"];
			ziflowStatus: MONDAY_TYPES["status"];
			channels: { linked_item_ids: string[]; display_value: string };
			products: { linked_item_ids: string[]; display_value: string };
		}>({ columnIds: Object.values(PROJECT_COLUMNS), itemIds: projectIds });

		const choices = await getStatusChoicesFromServer({
			data: {
				boardId: BOARDS_KEY_MAPPER_ENUM.tasks,
				columnId: TASKS_COLUMNS.status,
			},
		});

		const projects = baseProjects.map((proj) => ({
			...proj,
			statusGlobal: formatGlobalStatus(proj.statusGlobal, choices),
		}));

		const allSubitemNames = new Set(
			operation?.subitems
				?.filter((item) => item.status?.label !== "DELETED")
				.map((i) => `${i.name}§${i.timeline}`),
		);
		const formattedSubitem = Array.from(allSubitemNames).map((fullName) => {
			const [name, rawRimeline] = fullName.split("§");
			const timeline =
				rawRimeline === "null" || rawRimeline === "undefined"
					? null
					: rawRimeline;
			const concernedSubItems = operation?.subitems?.filter(
				(i) =>
					i.status?.label !== "DELETED" &&
					i?.name === name &&
					i?.timeline === timeline,
			);
			return {
				name,
				originalId: originalSubItems.find((subItem) => subItem.name === name)
					?.id,
				quantity: concernedSubItems.length,
				ids: concernedSubItems.map((s) => s.id),
				deliveryDate: timeline,
			};
		});

		return {
			...operation,
			canEdit:
				Object.entries(context.boards).find(
					([_, id]) => id === operation.board.id,
				)?.[0] === "briefs",
			thumbnail: operation.thumbnail?.[0],
			messages: filterMessages(operation.updates),
			expenses: computeMondayString(operation.expenses),
			versionCount: computeMondayString(operation.versionCount),
			subitems: formattedSubitem,
			projects: projects,
		};
	});

export const getOperationDetailsFromServer = wrapWithUseQuery(
	"operationDetails",
	handler,
);

export type OperationDetails = Awaited<
	ReturnType<typeof getOperationDetailsFromServer>
>;
